/**
 * RPS-RISK-EDIT-MODAL
 * Situations a risque d'une fonction pour un axe RPS et son evaluation sur quatre niveaux ; vide, l'axe est retire.
 */
import Chip from "@/components/chip"
import Modal from "@/components/modal"
import TextList from "@/components/text-list"
import { useAppDispatch } from "@/core/root-reducer"
import { STATUS_SAVING } from "@/redux/_status.types"
import { useState } from "react"
import populationStore from "../../model/population.store"
import { Population } from "../../model/population.types"
import { getRpsRisk, getRpsRiskLevels, RPS_RISK_AXES, RpsRiskAxis, setRpsRisk } from "../../model/rps-risk"

interface Props {
  population: Population
  axis: RpsRiskAxis
  onClose: () => void
}

export default function RpsRiskEditModal({ population, axis, onClose }: Props) {
  const dispatch = useAppDispatch()
  const current = getRpsRisk(population.rpsRisks, axis)
  const levels = getRpsRiskLevels(axis)
  const axisLabel = RPS_RISK_AXES.find((item) => item.id === axis)!.label
  const [situations, setSituations] = useState<string[]>(current?.situations ?? [])
  const [level, setLevel] = useState<number | null>(
    current?.level == null ? null : Math.min(current.level, levels.length - 1)
  )
  const [isSaving, setIsSaving] = useState(false)

  async function save() {
    setIsSaving(true)
    dispatch(
      populationStore.actions.edit({
        key: "rpsRisks",
        value: setRpsRisk(population.rpsRisks, { axis, situations, level }),
        idInList: population.id
      })
    )
    try {
      await dispatch(populationStore.thunks.updateEntity()).unwrap()
      onClose()
    } catch {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      title={axisLabel}
      isLarge
      onClose={onClose}
      contentClassName="!overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      onNext={save}
      status={isSaving ? STATUS_SAVING : undefined}>
      <div className="w-full">
        <div className="flex items-start justify-between gap-[24px] py-[4px]">
          <div className="flex min-w-0 flex-wrap gap-[8px]">
            {levels.map((item, index) => (
              <Chip key={index} color={item.color} isOutlineColored={index !== level} onClick={() => setLevel(index)}>
                {item.label}
              </Chip>
            ))}
          </div>
          <div className="ml-auto shrink-0">
            <Chip color="#8C8C8C" isOutlineColored={level !== null} onClick={() => setLevel(null)}>
              Non défini
            </Chip>
          </div>
        </div>
        <div className="mt-[28px]">
          <TextList
            title={`Situations à risque pour « ${population.name} »`}
            values={situations}
            onChange={setSituations}
            assistantIdeaPrompt={`Propose une situation de travail présentant un risque psychosocial sur le thème « ${axisLabel} » pour le métier ou la fonction « ${population.name} ». Format TL;DR : une seule phrase de 25 mots maximum, concrète et adaptée à ce métier. Va directement à la situation, sans introduction ni développement. Réponds uniquement avec la situation de travail, sans titre, sans mention « TL;DR », sans liste ni mesures de prévention. Présente cet exemple comme une situation possible, sans affirmer qu'elle existe dans cet établissement.`}
            getAssistantImprovePrompt={(value) =>
              `Résume cette situation de travail en mode TL;DR : une seule phrase claire et concrète de 25 mots maximum, sans introduction ni mention « TL;DR », sur le thème RPS « ${axisLabel} » pour le métier ou la fonction « ${population.name} ». Conserve les faits, sans inventer d'éléments et sans ajouter de mesures de prévention. Réponds uniquement avec le texte reformulé.\n\nSituation :\n${value}`
            }
          />
        </div>
      </div>
    </Modal>
  )
}
